import { useCallback, useMemo } from "react";
import { useQuery } from "react-query";

import { LicensesService } from "~/entity/Licenses/api/service";
import { ILicensesOverviewParams } from "~/entity/Licenses/model/schema";

import { TPositionFormItem } from "./types";

const PAGE_SIZE = 100;

export type TTrainingLicenseInfo = {
  freeCount: number;
  assigned: boolean;
};

type TOwnerLicenses = {
  freeByPosition: Record<number, number>;
  assignedByPosition: Record<number, boolean>;
};

const getPositionOwnerKey = (position: TPositionFormItem): string | null => {
  const dealer = position.dealers && position.dealers[0];
  const value = dealer && dealer.dealer && dealer.dealer.value;

  if (!value) {
    return null;
  }

  return dealer.organizationType === "PARTNER"
    ? `organization:${value}`
    : `dealer:${value}`;
};

const ownerKeyToParams = (key: string): ILicensesOverviewParams => {
  if (key.indexOf("organization:") === 0) {
    return { organizationId: key.slice("organization:".length) };
  }

  return { dealerId: Number(key.slice("dealer:".length)) };
};

const fetchFreeLicensesByPosition = async (
  params: ILicensesOverviewParams,
): Promise<Record<number, number>> => {
  const freeByPosition: Record<number, number> = {};
  let page = 0;
  let total = Infinity;

  while (page === 0 || page * PAGE_SIZE < total) {
    const { data } = await LicensesService.GET.positionGroups({
      ...params,
      page,
      count: PAGE_SIZE,
    });

    if (data.level !== "positions" || data.rows.length === 0) {
      break;
    }

    total = data.total;

    data.rows.forEach((row) => {
      (row.positions || []).forEach((position) => {
        if (position.instanceId != null) {
          freeByPosition[position.instanceId] =
            (freeByPosition[position.instanceId] || 0) + (row.available || 0);
        }
      });
    });

    page += 1;
  }

  return freeByPosition;
};

const fetchAssignedPositionIds = async (
  params: ILicensesOverviewParams,
  employeeId: number,
): Promise<Record<number, boolean>> => {
  const assignedByPosition: Record<number, boolean> = {};
  let page = 0;
  let total = Infinity;

  while (page === 0 || page * PAGE_SIZE < total) {
    const { data } = await LicensesService.GET.employees({
      ...params,
      employeeId,
      page,
      count: PAGE_SIZE,
    });

    if (data.level !== "employees" || data.rows.length === 0) {
      break;
    }

    total = data.total;

    data.rows.forEach((row) => {
      if (row.status === "assigned" && row.positionInstanceId != null) {
        assignedByPosition[row.positionInstanceId] = true;
      }
    });

    page += 1;
  }

  return assignedByPosition;
};

export const useTrainingLicenses = (
  positions: TPositionFormItem[] = [],
  employeeId?: string,
) => {
  const ownerKeys = useMemo(() => {
    const keys: string[] = [];

    positions.forEach((position) => {
      const key = getPositionOwnerKey(position);

      if (key && keys.indexOf(key) === -1) {
        keys.push(key);
      }
    });

    return keys;
  }, [positions]);

  const { data, isSuccess } = useQuery(
    [
      LicensesService._routes.licenses,
      "training",
      ownerKeys,
      employeeId || null,
    ],
    async (): Promise<Record<string, TOwnerLicenses>> => {
      const result: Record<string, TOwnerLicenses> = {};

      await Promise.all(
        ownerKeys.map(async (key) => {
          const params = ownerKeyToParams(key);
          const [freeByPosition, assignedByPosition] = await Promise.all([
            fetchFreeLicensesByPosition(params),
            employeeId
              ? fetchAssignedPositionIds(params, Number(employeeId))
              : Promise.resolve({} as Record<number, boolean>),
          ]);

          result[key] = { freeByPosition, assignedByPosition };
        }),
      );

      return result;
    },
    { enabled: ownerKeys.length > 0 },
  );

  const loaded = ownerKeys.length === 0 || isSuccess;

  const getLicenseInfo = useCallback(
    (position: TPositionFormItem): TTrainingLicenseInfo | null => {
      const key = getPositionOwnerKey(position);
      const positionId = Number(position.position && position.position.value);

      if (!key || !positionId || !data) {
        return null;
      }

      const owner = data[key];

      if (!owner) {
        return null;
      }

      return {
        freeCount: owner.freeByPosition[positionId] || 0,
        assigned: Boolean(owner.assignedByPosition[positionId]),
      };
    },
    [data],
  );

  return { getLicenseInfo, loaded };
};
